import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaBus, FaClock } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './AdiKailashTour.css';

const AdiKailashTour = () => {
  return (
    <>
      <Helmet>
        <title>Adi Kailash Tour - NomadTrailz</title>
        <meta name="description" content="Experience the sacred Adi Kailash pilgrimage with NomadTrailz. Trek to Mount Adi Kailash and witness the divine Om Parvat at 4,572 meters." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755008359/kobdzjpdbuta54fnretj.jpg" 
              alt="Adi Kailash" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Adi Kailash Tour
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred pilgrimage to Mount Adi Kailash and Om Parvat at 4,572 meters
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Adi Kailash is one of the most sacred pilgrimage sites in the Himalayas, often called the "Little Kailash" 
                    or "Chhota Kailash". This spiritual journey takes you through the pristine landscapes of Uttarakhand to reach 
                    Mount Adi Kailash at 4,572 meters, where you'll witness the divine Om Parvat with its natural snow formation 
                    of the sacred "ॐ" symbol. Experience the spiritual ambiance and breathtaking natural beauty with comfortable 
                    accommodations and expert guidance.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred Mount Adi Kailash at 4,572 meters</li>
                    <li>Divine Om Parvat darshan with natural snow formation</li>
                    <li>Jageshwar Temple and Bhimtal visit</li>
                    <li>International borders of Nepal & China</li>
                    <li>Parvati Temple and Parvati Sarovar Kund</li>
                    <li>Panoramic views of divine Adi Kailash</li>
                    <li>High altitude trekking and acclimatization</li>
                    <li>Cultural immersion in remote Himalayan villages</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Kathgodam To Pithoragarh (180 KMs / 7-8 Hrs) 1514 m</h4>
                    <ul>
                      <li>180 Km Drive to Pithoragarh through Almora</li>
                      <li>Early start, visit Bhimtal, Neem Kiroli Baba temple at Kanchi Dham, then Jageshwar Temple and Pithoragarh</li>
                      <li>Dinner and overnight stay at a Decent 3-star hotel (twin/triple share basis)</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Pithoragarh To Dharchula (100 Kms / 3 Hrs Approx.) 915 m</h4>
                    <ul>
                      <li>Morning after breakfast, proceed to Dharchula via Baling</li>
                      <li>Journey is about 100kms, 3 hours by jeep</li>
                      <li>Complete paperwork for Medical & Inner line permit</li>
                      <li>Possible visit to Nepal (international borders of Nepal & China)</li>
                      <li>Overnight stay in rest house/tents, provision of sacks</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Dharchula To Gunji / Nabi (80km) 3000 m</h4>
                    <ul>
                      <li>Morning after breakfast, proceed to Gunji by Jeep</li>
                      <li>Arrive Gunji in the afternoon</li>
                      <li>En-route, visit villages like Chialekh, Garbyang, Napalchu in the Nabi Region</li>
                      <li>Overnight stay in rest house/tents</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Gunji-Kalapani-Navidhang-Nabi (Om Parvat darshan) (3050 m)</h4>
                    <ul>
                      <li>Drive from Kalapani to Navidhang for Om Parvat darshan (35 km, 45 min by vehicle)</li>
                      <li>See Om Parvat with the natural shape of snow ॐ (Aum)</li>
                      <li>Perform Darshan/puja/meditation at Om Parvat, then return to NABI</li>
                      <li>Overnight stay in camp beside the stream</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Nabi-Jyolingkong visit Mount Adi Kailash (30 Kms / 1 Hrs approx.) 4572 m</h4>
                    <ul>
                      <li>Morning after breakfast, visit nearby places like Kunti Parvat</li>
                      <li>Walk around village Kuti</li>
                      <li>Second half proceeds to Jollingkong for acclimatization (cold weather & high altitude conditions)</li>
                      <li>Option to visit Gaurikund</li>
                      <li>Overnight stay in the guest house</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Nabi - Dharchula (14 KMs / 1 Hrs) 3700 m</h4>
                    <ul>
                      <li>Early morning, see the PANORAMIC Bestowing Ambiance of the divine Adi Kailash</li>
                      <li>Visit Parvati Temple (3 km trek from camp site)</li>
                      <li>Option for Parikrama of Parvati Sarovar Kund</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: Dharchula to Kathgodam (270 KMs / 7/8 Hrs)</h4>
                    <ul>
                      <li>Morning after breakfast, check out and drive to Kathgodam</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Accommodation (Basic Homestay & Camps)</li>
                    <li><FaCheck className="check-icon" />Meals (specifically 6 Breakfast, 6 lunch, and 6 Dinner)</li>
                    <li><FaCheck className="check-icon" />Trek Guide, Driver, Road tax, Vehicle Parking</li>
                    <li><FaCheck className="check-icon" />Transportation in Tempo traveller</li>
                    <li><FaCheck className="check-icon" />Equipment's for climbing (if required)</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Any personal Expenses / Adventure activities</li>
                    <li><FaTimes className="times-icon" />Anything not mentioned in the itinerary</li>
                    <li><FaTimes className="times-icon" />Any kind of entry tickets/fees</li>
                    <li><FaTimes className="times-icon" />Any Meals / Drinks other than Inclusion</li>
                    <li><FaTimes className="times-icon" />5%GST</li>
                    <li><FaTimes className="times-icon" />Personal Porter</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 18,999</span>
                    <span className="duration">7 Days / 6 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Adi Kailash, Uttarakhand</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Challenging</span>
                    </div>
                  </div>

                  <div className="pricing-options">
                    <h4>Cost Per Person</h4>
                    <div className="price-option">
                      <span className="option">TRIPLE SHARE</span>
                      <span className="amount">₹ 18,999 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">DOUBLE SHARE</span>
                      <span className="amount">₹ 21,999 + 5% GST</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaBus className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Natraj chowk Rishikesh</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaClock className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Time:</span>
                        <span className="logistics-value">7:00 AM</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaMapMarkerAlt className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Maximum Altitude:</span>
                        <span className="logistics-value">6,310 meters</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaStar className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Trek Distance:</span>
                        <span className="logistics-value">Approx. 40 km</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Adi Kailash Sacred Pilgrimage"
                  tourPrice="₹ 18,999"
                  tourDuration="7 Days / 6 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default AdiKailashTour;
